import React, {useMemo, useState} from 'react';
import {Script, ScriptStrategy, graphql} from 'gatsby';
import {IntlProvider} from 'react-intl';
import {Layout} from '../components/layout/Layout';
import {Container as Blocks} from '../components/dato/Container';
import {ErrorBoundary} from '../components/boundary/error/ErrorBoundary';
import {StoreUtmParams} from '../components/cookie/StoreUtmParams';
import {getDefaultOpenGraphImage} from '../utils/getDefaultOpenGraphImage';
import {config} from '../../config';
import {SectionMaxWidth, Section} from '../components/layout/Section';
import {Disclaimer} from '../components/Disclaimer';
import type {BasePage, PageContext as ContextProps, Location as LocationProps} from '../types/page';
import {Breadcrumbs} from '../components/navigation/BreadCrumbs';
import {Survicate} from '../scripts/Survicate';
import {localeToPath} from '../utils/localeToPath';
import {isSurvicatePage} from '../utils/isSurvicatePage';
import {Template} from '../types/block';
import {PageContext, PageContextSectionEnum} from '../context/PageContext';
import {getStructuredData} from '../utils/pageUtils';
import {TemplateInfoEnum} from '../context/TemplateInfoContext';
import {getTableOfContents} from '../components/blogPost/TableOfContents';
import {HtmlHead} from '../components/HtmlHead';
import {StructuredData} from '../components/StructuredData';

type Props = {
  pageContext: ContextProps;
  location: LocationProps;
  path: string;
  data: {
    relatedBlogPostsByTag?: Queries.PageTemplateQueryQuery['relatedBlogPostsByTag'];
    datoCmsPage: BasePage;
  };
};
const PageTemplate = (props: Props) => (
  <ErrorBoundary>
    <IntlProvider locale={props.pageContext.locale} messages={props.pageContext.messages}>
      <PageLayout {...props} />
    </IntlProvider>
  </ErrorBoundary>
);

export const Head = (props: Props) => (
    <HtmlHead
      template='page'
      slugLocales={props.pageContext.slugLocales}
      currentLocale={props.pageContext.locale}
      currentPathname={props.location.pathname}
      noIndex={props.data.datoCmsPage.metaNoIndex}
      title={props.data.datoCmsPage.titleForSeo}
      description={props.data.datoCmsPage.metaDescription}
      canonicalPathname={props.pageContext.canonicalPathname}
      openGraphImage={props.data.datoCmsPage.openGraphImage || getDefaultOpenGraphImage()}
      openGraphType='website'
      enableVwo={props.data.datoCmsPage.enableVwo}
    />
  );
const PageLayout = (props: Props) => {
  const [showYearlyPrice, setShowYearlyPrice] = useState(true);
  const toc = useMemo(() => getTableOfContents(props.data.datoCmsPage), []);
  const templateInfo = {
    [TemplateInfoEnum.MODEL_NAME]: props.data.datoCmsPage.model.name,
    [TemplateInfoEnum.MODEL_ORIGINAL_ID]: props.data.datoCmsPage.model.originalId,
    [TemplateInfoEnum.ORIGINAL_ID]: props.data.datoCmsPage.originalId,
    [TemplateInfoEnum.SLUG]: props.data.datoCmsPage.slug
  };

  // Group blog posts under their tag (as key)
  // slicing to length config.relatedBlogPostLimit as a safe net
  // only since group by already limits for us
  const relatedBlogPostsByTag =
    props.data.relatedBlogPostsByTag?.group.reduce((acc, group) => {
      acc[group.nodes[0].tags.name] = group.nodes.slice(0, config.relatedBlogPostLimit);
      return acc;
    }, {}) || {};

  const navigationHidden = props.data.datoCmsPage.hideNavigations || props.data.datoCmsPage.hideNavigationsKeepHeaderWithLogo;

  const showBreadcrumbs = !navigationHidden && props.data.datoCmsPage.titleForBreadcrumb && props.data.datoCmsPage.parentPage;

  const {faqSchema, pageSchema} = getStructuredData(props.data);

  const isSurvicatePageMemoized = React.useMemo(
    () =>
      isSurvicatePage({
        locale: localeToPath(props.pageContext.locale),
        path: props.path
      }),
    [props.path]
  );

  const hasStorylane = props.data.datoCmsPage.content.some(block => block.__typename === 'DatoCmsStorylaneDemo');

  return (
    <PageContext.Provider
      value={{
        [PageContextSectionEnum.PRICE]: [showYearlyPrice, setShowYearlyPrice],
        [PageContextSectionEnum.TOC]: toc
      }}
    >
      <Layout
        slugLocales={props.pageContext.slugLocales}
        currentPathname={props.location.pathname}
        hideNavigationsKeepHeaderWithLogo={props.data.datoCmsPage.hideNavigationsKeepHeaderWithLogo}
        hideNavigations={props.data.datoCmsPage.hideNavigations}
        bannerText={props.data.datoCmsPage.bannerText}
        showTopBanner={props.data.datoCmsPage.showTopBanner}
        templateInfo={templateInfo}
      >
        {showBreadcrumbs && <Breadcrumbs data={props.data.datoCmsPage} />}
        <Blocks
          content={props.data.datoCmsPage.content}
          blockSpecificData={props.pageContext.blockSpecificData}
          relatedBlogPostsByTag={relatedBlogPostsByTag}
          template={Template.PAGE}
        />

        {props.data.datoCmsPage.showDisclaimer && (
          <Section maxWidth={SectionMaxWidth.MEDIUM}>
            <Disclaimer />
          </Section>
        )}
        <StoreUtmParams />
      </Layout>
      <StructuredData jsonString={pageSchema} dataTestId='page-schema' />
      <StructuredData jsonString={faqSchema} dataTestId='faq-schema' />
      {isSurvicatePageMemoized && <Survicate />}
      {hasStorylane && <Script strategy={ScriptStrategy.idle} defer src='https://js.storylane.io/js/v1/storylane.js' />}
    </PageContext.Provider>
  );
};

export default PageTemplate;

export const pageQuery = graphql`
  query PageTemplateQuery($originalId: String!, $locale: String!, $relatedBlogPostsBlocksTags: [String]!, $relatedBlogPostLimit: Int!) {
    datoCmsPage(locales: {eq: $locale}, originalId: {eq: $originalId}, locale: $locale) {
      originalId
      model {
        originalId
        name
      }
      enableVwo
      metaNoIndex
      hideNavigationsKeepHeaderWithLogo
      hideNavigations
      showDisclaimer
      showTopBanner
      bannerText
      metaDescription
      titleForSeo
      titleForBreadcrumb
      parentPage {
        slug
        titleForBreadcrumb
        parentPage {
          slug
          titleForBreadcrumb
          parentPage {
            slug
            titleForBreadcrumb
            parentPage {
              slug
              titleForBreadcrumb
            }
          }
        }
      }
      openGraphImage {
        height
        width
        url
      }
      structuredData
      slug
      content {
        ...blocksPage
      }
    }
    relatedBlogPostsByTag: allDatoCmsBlogpost(
      sort: {meta: {publishedAt: DESC}}
      filter: {locales: {eq: $locale}, tags: {name: {ne: null, in: $relatedBlogPostsBlocksTags}}}
      locale: $locale
    ) {
      group(field: {tags: {name: SELECT}}, limit: $relatedBlogPostLimit) {
        nodes {
          ...post
        }
      }
    }
  }
`;
